import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';

import { useUserPermissionStatus } from 'selectors/user';
import type { SurfDataPoints } from 'types/surf';
import type { SurfHeight } from 'types/units';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import useSetupCurveGraph from '../../hooks/useSetupCurveGraph';

import styles from './DaySelectorGraph.module.scss';

const cx = classNames.bind(styles);
interface Props {
  data: SurfDataPoints;
  height: number;
  spotId: string;
  isPaywallVisible?: boolean;
  parentRef: any;
  units: SurfHeight;
  chartIdPrefix?: string;
}

// This is the 16-Day Selector at the top of the Spot Page
const DaySelectorGraph: React.FC<Props> = ({
  data,
  height = 0,
  spotId,
  isPaywallVisible,
  parentRef,
  units,
  chartIdPrefix,
}) => {
  const { hasCoreForecastPermissions } = useUserPermissionStatus();

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasCoreForecastPermissions,
  });

  const waveData = useMemo(
    () =>
      hasCoreForecastPermissions ? data : data.slice(0, (data.length / 16) * forecastDaysAllowed),
    [forecastDaysAllowed, data, hasCoreForecastPermissions],
  );

  const { setupCurveGraph } = useSetupCurveGraph({
    spotId,
    data: waveData,
    parentRef,
    height,
    // we want to include 4 data points per day (midnight, 6am, noon, 6pm)
    // Premium users have hourly data where non-premium have 3-hourly which is why this differs between the two
    smoothingStrength: hasCoreForecastPermissions ? 6 : 2,
    units,
    chartIdPrefix,
  });

  useEffect(() => {
    setupCurveGraph();
  }, [height, setupCurveGraph]);

  return (
    <div className={cx(['day-selector-chart-wrapper', 'daySelectorChartWrapper'])}>
      <div
        className={cx([
          'day-selector-chart-content',
          'daySelectorChart',
          {
            'daySelectorChart--paywall': isPaywallVisible,
            'daySelectorChart--paywall-V3': isPaywallVisible,
          },
        ])}
      />
    </div>
  );
};

export default DaySelectorGraph;
